import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./Modeling3DPages.css";

const pages = {
    character: {
        slug: "character-modeling",
        eyebrow: "Blender Character Modeling",
        title: "Game Character Modeling for Heroes, NPCs, Enemies & Avatars",
        description: "Oengines Games LLP creates Blender 3D character models for games, including stylized heroes, NPCs, enemies, avatars, skins, outfits, accessories, and production-ready game assets.",
        keywords: "Blender character modeling, game character modeling, 3D character modeling for games, game avatar modeling, NPC character models, game-ready character assets",
        layout: "hunter",
        heroImage: "assets/img/GameDevelopment/Action/character.png",
        stats: ["Heroes", "NPCs", "Enemies", "Avatars"],
        services: [
            ["Playable Characters", "Stylized or semi-realistic game characters with clean proportions, readable silhouettes, optimized meshes, and animation-aware topology."],
            ["NPC & Enemy Models", "Citizens, enemies, bosses, companions, merchants, guards, and story characters built for gameplay and world population."],
            ["Skins & Accessories", "Outfits, helmets, armor, hair, bags, weapons, collectible skins, and alternate visual styles for progression or monetization."],
            ["Game Export Setup", "Blender source files, clean mesh organization, naming, scale checks, material slots, and engine handoff support."]
        ],
        showcase: {
            title: "Character Asset Direction",
            text: "We model characters with gameplay distance, animation needs, mobile performance, customization, and store presentation in mind.",
            items: ["Strong silhouette", "Animation-friendly topology", "Skin and outfit variants", "LOD-ready structure"]
        },
        production: [
            ["Reference & Style", "Character style, proportion, outfit, gameplay role, and visual target are defined before modeling starts.", "Art Direction"],
            ["Base Mesh", "Clean Blender base mesh with body, clothing, accessories, and animation-safe edge flow.", "Modeling"],
            ["Game Optimization", "Mesh cleanup, polygon budget review, material grouping, and optional LOD planning.", "Performance"],
            ["Handoff", "Source files, exports, texture references, naming, and implementation notes for developers.", "Delivery"]
        ],
        pipeline: ["Reference board", "Blockout", "Detailed model", "Optimization", "Export package"],
        deliverables: ["Blender file", "FBX/GLTF export", "Character mesh", "Outfit variants", "Accessory meshes", "Material slots", "LOD guidance", "Engine handoff notes"],
        faqs: [
            ["Do you create character models only in Blender?", "Yes. Our 3D modeling pages are planned around Blender modeling, cleanup, export, and game handoff."],
            ["Can you make optimized characters for mobile games?", "Yes. We can plan polygon count, texture usage, material slots, and LOD needs for mobile performance."],
            ["Can characters be prepared for rigging?", "Yes. We model with rigging and animation needs in mind, including topology around shoulders, knees, elbows, neck, and face."]
        ]
    },
    environment: {
        slug: "environment-modeling",
        eyebrow: "Blender Environment Modeling",
        title: "Game Environment Modeling for Worlds, Levels, Arenas & Maps",
        description: "We create Blender environment models for mobile, PC, web, and console games, including stylized worlds, arenas, racing tracks, fantasy maps, rooms, props, and modular level kits.",
        keywords: "Blender environment modeling, game environment modeling, 3D game world modeling, level art modeling, modular environment assets, game map modeling",
        layout: "world",
        heroImage: "assets/img/GameDevelopment/Battle-Royale-Game-th.png",
        stats: ["Worlds", "Arenas", "Maps", "Level Kits"],
        services: [
            ["Level Blocks", "Playable spaces, pathways, cover areas, landmarks, scene layout, and early world structure for game production."],
            ["Modular Kits", "Walls, floors, platforms, stairs, rocks, terrain pieces, buildings, and reusable environment modules."],
            ["Arena & Map Assets", "Battle arenas, racing tracks, sports grounds, board-game environments, casual game worlds, and fantasy locations."],
            ["Performance Planning", "Scene organization, draw-call awareness, LOD strategy, collider-ready shapes, and optimized asset grouping."]
        ],
        showcase: {
            title: "World Building System",
            text: "Environment art is designed as a reusable game system, so teams can build levels faster while keeping visual quality and performance under control.",
            items: ["Modular level pieces", "Gameplay landmark planning", "Optimized scene hierarchy", "Collision-friendly forms"]
        },
        production: [
            ["Gameplay Layout", "We define player routes, camera view, playable bounds, and important landmarks.", "Level Design"],
            ["Modular Modeling", "Reusable environment pieces are created in Blender for faster map production.", "Asset Kit"],
            ["Scene Dressing", "Props, terrain pieces, architecture, and visual storytelling details are added.", "World Art"],
            ["Optimization Pass", "Mesh cleanup, material grouping, LOD notes, and engine handoff are prepared.", "Performance"]
        ],
        pipeline: ["Map goal", "Greybox", "Modular kit", "Scene dressing", "Export"],
        deliverables: ["Blender scene", "Modular kit", "Environment props", "Map assets", "FBX/GLTF files", "Scale notes", "LOD notes", "Scene hierarchy"],
        faqs: [
            ["Can you create complete game environments in Blender?", "Yes. We create Blender-based environments, modular kits, maps, arenas, terrain pieces, and level assets."],
            ["Can environments be optimized for mobile games?", "Yes. We can plan mesh density, material usage, object grouping, and LOD needs for mobile-friendly scenes."],
            ["Can you create reusable modular assets?", "Yes. We can build modular walls, floors, props, rocks, buildings, platforms, and reusable level pieces."]
        ]
    },
    props: {
        slug: "props-weapons-modeling",
        eyebrow: "Blender Props & Weapons",
        title: "Game Props and Weapon Modeling for Inventories, Stores & Combat",
        description: "Oengines creates Blender props and weapon models for games, including guns, swords, tools, crates, collectibles, power-ups, inventory objects, reward items, and monetization assets.",
        keywords: "Blender weapon modeling, game props modeling, 3D weapon assets, game prop assets, inventory item modeling, game store item modeling",
        layout: "forge",
        heroImage: "assets/img/GameDevelopment/Product_Poker.png",
        stats: ["Weapons", "Props", "Items", "Rewards"],
        services: [
            ["Weapon Models", "Swords, guns, bows, shields, sci-fi weapons, fantasy weapons, skins, attachments, and upgrade variants."],
            ["Gameplay Props", "Crates, keys, tools, machines, collectibles, pickups, power-ups, traps, doors, and interactive game objects."],
            ["Store Items", "Premium items, reward assets, chest contents, inventory previews, battle-pass objects, and digital product models."],
            ["Clean Asset Handoff", "Blender files, export formats, pivot checks, material slots, naming, and scale consistency for implementation."]
        ],
        showcase: {
            title: "Combat and Inventory Asset System",
            text: "Props and weapons are modeled for gameplay clarity, inventory previews, animation readiness, upgrade systems, and store presentation.",
            items: ["Readable item silhouette", "Attachment-ready structure", "Inventory preview angles", "Upgrade variant planning"]
        },
        production: [
            ["Item Role", "We define whether the asset is for combat, inventory, store, environment, reward, or interaction.", "Design"],
            ["Blender Model", "The item is modeled with readable form, correct scale, and clean geometry.", "Modeling"],
            ["Variant Planning", "Skins, rarity colors, attachments, upgrades, damage states, or collectible variants are prepared.", "LiveOps"],
            ["Export Package", "Pivots, naming, material slots, and engine-ready exports are checked before handoff.", "Delivery"]
        ],
        pipeline: ["Item brief", "Shape blockout", "Detail pass", "Variant pass", "Export"],
        deliverables: ["Weapon models", "Prop models", "Inventory assets", "Reward items", "Variant meshes", "FBX/GLTF export", "Material slots", "Handoff notes"],
        faqs: [
            ["Can you create weapon models in Blender?", "Yes. We model weapons, props, inventory items, collectibles, and store assets in Blender."],
            ["Can weapons include skins and variants?", "Yes. We can prepare upgrade variants, rarity versions, material swaps, attachments, and collectible skins."],
            ["Can props be prepared for interaction?", "Yes. We can model props with pivot placement, scale, clean naming, and engine handoff needs in mind."]
        ]
    },
    vehicles: {
        slug: "vehicle-modeling",
        eyebrow: "Blender Vehicle Modeling",
        title: "Vehicle Modeling for Racing, Combat, Simulator & Open-World Games",
        description: "We create Blender vehicle models for games, including cars, bikes, trucks, sci-fi vehicles, boats, aircraft, racing assets, combat vehicles, and customization-ready models.",
        keywords: "Blender vehicle modeling, game vehicle modeling, racing car 3D model, game car modeling, vehicle assets for games, low poly vehicles",
        layout: "garage",
        heroImage: "assets/img/GameDevelopment/Racing/Racing.png",
        stats: ["Cars", "Bikes", "Aircraft", "Skins"],
        services: [
            ["Racing Vehicles", "Cars, karts, bikes, trucks, off-road vehicles, tuning parts, wheels, spoilers, and racing game assets."],
            ["Combat & Sci-Fi Vehicles", "Tanks, drones, aircraft, hover vehicles, armored units, and stylized futuristic vehicle models."],
            ["Customization Sets", "Body kits, paint variants, decals, wheel swaps, damage variants, and upgrade-friendly mesh organization."],
            ["Game-Ready Exports", "Blender source files, pivot checks, scale, wheel separation, material slots, and export-ready structure."]
        ],
        showcase: {
            title: "Garage-Ready Vehicle Assets",
            text: "Vehicle models need clean silhouette, readable parts, correct pivots, customization support, and strong presentation for garage and store screens.",
            items: ["Wheel and pivot checks", "Garage preview composition", "Body kit variants", "LOD and collision notes"]
        },
        production: [
            ["Vehicle Purpose", "Racing, combat, simulator, open-world, or garage presentation requirements are defined.", "Gameplay"],
            ["Part Modeling", "Body, wheels, attachments, cockpit, weapons, and upgrade parts are modeled in Blender.", "Asset Build"],
            ["Customization", "Paint, decals, skins, body kits, wheels, and variant planning are organized.", "Monetization"],
            ["Handoff", "Scale, pivots, separated parts, material slots, and export files are prepared.", "Integration"]
        ],
        pipeline: ["Vehicle brief", "Blockout", "Part modeling", "Variant setup", "Export"],
        deliverables: ["Vehicle mesh", "Wheel parts", "Body variants", "Damage variants", "Garage preview asset", "FBX/GLTF export", "Pivot notes", "LOD guidance"],
        faqs: [
            ["Can you create vehicle models for racing games?", "Yes. We create Blender vehicle models for racing, simulator, combat, open-world, and stylized games."],
            ["Can vehicle parts be separated for gameplay?", "Yes. Wheels, weapons, body kits, attachments, and upgrade parts can be separated and named for implementation."],
            ["Can vehicles be optimized for mobile?", "Yes. We can plan low-poly, mid-poly, LOD, material grouping, and texture usage based on the target platform."]
        ]
    },
    architecture: {
        slug: "architectural-modeling",
        eyebrow: "Blender Architectural Models",
        title: "Architectural 3D Models for Game Worlds, Buildings & Digital Spaces",
        description: "Oengines creates Blender architectural models for game cities, interiors, buildings, arenas, metaverse spaces, VR scenes, training simulations, and digital environments.",
        keywords: "Blender architectural modeling, game architecture modeling, 3D building models for games, environment architecture assets, game interiors modeling",
        layout: "citadel",
        heroImage: "assets/img/bg/Model.jpg",
        stats: ["Buildings", "Interiors", "Cities", "Digital Spaces"],
        services: [
            ["Game Buildings", "Houses, towers, shops, castles, arenas, factories, sci-fi structures, and city pieces for game environments."],
            ["Interior Spaces", "Rooms, lobbies, corridors, stores, casinos, offices, training spaces, and interactive interior layouts."],
            ["Stylized Architecture", "Low-poly, casual, fantasy, realistic, sci-fi, mobile-friendly, and theme-based architectural assets."],
            ["Scene Handoff", "Blender scenes, modular building pieces, scale references, collision-friendly geometry, and export notes."]
        ],
        showcase: {
            title: "Playable Architecture for Digital Worlds",
            text: "Architecture for games must support camera movement, player flow, collision, performance, and visual storytelling across levels.",
            items: ["Modular building kits", "Interior path planning", "Collision-aware forms", "Theme-based city pieces"]
        },
        production: [
            ["Space Planning", "We define scale, camera needs, player path, interior usage, and visual theme.", "Planning"],
            ["Structure Modeling", "Buildings, rooms, walls, doors, windows, platforms, and architectural details are modeled.", "Modeling"],
            ["World Integration", "Assets are prepared for modular placement, city layout, interior flow, or VR/digital space use.", "Environment"],
            ["Optimization", "Meshes, materials, modular pieces, and export naming are cleaned for game teams.", "Handoff"]
        ],
        pipeline: ["Space brief", "Block model", "Architecture pass", "Scene polish", "Export"],
        deliverables: ["Building models", "Interior models", "Modular architecture", "Scene assets", "Scale notes", "FBX/GLTF export", "Collision notes", "Blender files"],
        faqs: [
            ["Can architectural models be used in games?", "Yes. We create Blender architectural assets for game worlds, interiors, VR scenes, simulations, and digital spaces."],
            ["Can you build modular buildings?", "Yes. We can create modular walls, floors, doors, windows, roofs, stairs, and reusable city pieces."],
            ["Can assets be optimized for real-time use?", "Yes. We plan geometry, materials, scale, and export structure for real-time game implementation."]
        ]
    },
    lowhigh: {
        slug: "low-poly-high-poly-assets",
        eyebrow: "Blender Low-Poly & High-Poly Assets",
        title: "Low-Poly and High-Poly Game Assets Built in Blender",
        description: "We create Blender low-poly and high-poly assets for games, including optimized mobile models, detailed hero assets, props, characters, environments, and bake-ready production models.",
        keywords: "Blender low poly assets, Blender high poly assets, game asset modeling, optimized 3D assets for games, high poly game models, low poly mobile game assets",
        layout: "dual",
        heroImage: "assets/img/GameDevelopment/3d-game-sprite1.png",
        stats: ["Low-Poly", "High-Poly", "LOD", "Bakes"],
        services: [
            ["Low-Poly Assets", "Mobile-friendly props, characters, environments, weapons, vehicles, and stylized models with controlled polygon budgets."],
            ["High-Poly Assets", "Detailed models for hero objects, sculpting references, normal map baking, close-up renders, and marketing presentation."],
            ["LOD Planning", "Multiple detail levels, simplified geometry, asset grouping, and performance-focused mesh versions."],
            ["Bake-Ready Models", "High-poly to low-poly workflows, clean naming, cage planning support, and texture handoff preparation."]
        ],
        showcase: {
            title: "Performance and Detail Balance",
            text: "We choose low-poly, high-poly, or hybrid workflows based on platform, camera distance, gameplay priority, and production budget.",
            items: ["Polygon budget planning", "High-to-low workflow", "LOD strategy", "Bake-ready structure"]
        },
        production: [
            ["Asset Priority", "We decide which models need high detail and which should stay lightweight.", "Planning"],
            ["Geometry Build", "Blender meshes are created according to low-poly, high-poly, or bake-ready goals.", "Modeling"],
            ["Optimization", "Topology, silhouettes, LOD versions, and material grouping are reviewed.", "Performance"],
            ["Delivery", "Files are named, organized, exported, and documented for development handoff.", "Handoff"]
        ],
        pipeline: ["Asset audit", "Poly target", "Model build", "LOD or bake prep", "Export"],
        deliverables: ["Low-poly models", "High-poly models", "LOD meshes", "Bake-ready files", "Optimized exports", "Polycount notes", "Blender sources", "Handoff notes"],
        faqs: [
            ["Do you create both low-poly and high-poly assets?", "Yes. We create low-poly optimized assets and high-poly detail assets in Blender."],
            ["Can you prepare LOD versions?", "Yes. We can create simplified versions or provide LOD planning based on platform and asset priority."],
            ["Can assets be prepared for baking?", "Yes. We can organize high-poly and low-poly assets for normal map and texture bake workflows."]
        ]
    },
    pbr: {
        slug: "pbr-texturing",
        eyebrow: "Blender PBR Texturing",
        title: "PBR Texturing for Game Assets, Props, Characters & Environments",
        description: "Oengines creates Blender PBR texturing workflows for game models, including materials for characters, weapons, vehicles, environments, props, store items, and real-time assets.",
        keywords: "Blender PBR texturing, game asset texturing, PBR materials for games, 3D texture creation, game prop texturing, real-time material workflow",
        layout: "material",
        heroImage: "assets/img/GameDevelopment/School-of-Dragons800x600.png",
        stats: ["Albedo", "Roughness", "Metallic", "Normals"],
        services: [
            ["Game Materials", "Stylized, realistic, fantasy, sci-fi, casual, metal, fabric, wood, stone, skin, armor, and weapon material styles."],
            ["Texture Sets", "Albedo, normal, roughness, metallic, emissive, opacity, masks, and texture packing guidance for real-time use."],
            ["Asset Presentation", "Materials tuned for inventory previews, hero renders, store items, character skins, and environment readability."],
            ["Optimization Support", "Texture size planning, atlas guidance, material slot cleanup, and performance-focused handoff notes."]
        ],
        showcase: {
            title: "Real-Time Material Direction",
            text: "PBR texturing is planned around engine lighting, camera distance, platform limits, store previews, and gameplay readability.",
            items: ["Texture channel planning", "Material slot cleanup", "Skin and rarity variants", "Real-time lighting checks"]
        },
        production: [
            ["Material Brief", "We define style, target platform, texture resolution, channel needs, and gameplay visibility.", "Direction"],
            ["Texture Creation", "PBR texture sets and material previews are built around the Blender asset.", "Texturing"],
            ["Variant Pass", "Colorways, rarity versions, team skins, damage states, or upgrade materials are prepared.", "LiveOps"],
            ["Export Notes", "Texture names, packed channels, material slots, and implementation guidance are delivered.", "Handoff"]
        ],
        pipeline: ["Material brief", "Texture pass", "Variant pass", "Optimization", "Handoff"],
        deliverables: ["PBR texture sets", "Material previews", "Texture variants", "Packed texture notes", "Blender material setup", "Resolution guidance", "Export notes", "Asset renders"],
        faqs: [
            ["Do you create PBR textures for game assets?", "Yes. We create PBR material workflows and texture sets for Blender game assets."],
            ["Can textures be optimized for mobile?", "Yes. We can plan texture resolution, atlas usage, material slots, and packed texture channels."],
            ["Can you create skin or rarity variants?", "Yes. We can prepare color variants, rarity styles, team skins, upgrades, and store-ready material options."]
        ]
    },
    uv: {
        slug: "uv-mapping",
        eyebrow: "Blender UV Mapping",
        title: "UV Mapping for Clean Game Textures, Bakes & Real-Time Assets",
        description: "We provide Blender UV mapping for game assets, including characters, props, weapons, vehicles, environments, modular kits, texture atlases, lightmap-friendly layouts, and bake-ready UVs.",
        keywords: "Blender UV mapping, game asset UV mapping, UV unwrap for games, texture atlas UVs, lightmap UV layout, bake-ready UV mapping",
        layout: "grid",
        heroImage: "assets/img/bg/process.png",
        stats: ["UVs", "Atlases", "Bakes", "Lightmaps"],
        services: [
            ["Asset UV Unwrap", "Characters, props, weapons, vehicles, buildings, modular kits, and environment assets unwrapped for game texturing."],
            ["Texture Atlas Planning", "Efficient UV packing for grouped props, UI store items, low-poly assets, and mobile-friendly texture usage."],
            ["Bake-Ready UVs", "UV layouts planned for normal maps, ambient occlusion, curvature, masks, and high-to-low asset baking."],
            ["Lightmap Support", "Clean secondary UV guidance for real-time lighting workflows where projects require lightmap-ready assets."]
        ],
        showcase: {
            title: "Texture Space Control",
            text: "Good UVs reduce texture waste, improve bake quality, and make real-time assets easier to texture, optimize, and maintain.",
            items: ["Clean unwraps", "Efficient packing", "Bake-ready islands", "Atlas-friendly layout"]
        },
        production: [
            ["Asset Review", "We review shape complexity, texture goals, seams, platform limits, and bake requirements.", "Audit"],
            ["UV Layout", "Seams, islands, texel density, and packing are handled in Blender.", "Unwrap"],
            ["Texture Planning", "Atlas, bake, material, or lightmap needs are checked before delivery.", "Optimization"],
            ["Handoff", "UV-ready Blender files, notes, and export files are prepared for texturing or engine use.", "Delivery"]
        ],
        pipeline: ["Asset review", "Seam plan", "UV unwrap", "Packing", "Export"],
        deliverables: ["UV layouts", "Atlas-ready files", "Bake-ready UVs", "Texel density notes", "Secondary UV notes", "Blender files", "Export files", "Texture handoff"],
        faqs: [
            ["Do you provide UV mapping in Blender?", "Yes. We unwrap, pack, and prepare UV layouts for Blender game assets."],
            ["Can UVs support texture baking?", "Yes. We can prepare bake-ready UVs for normal, AO, curvature, mask, and high-to-low workflows."],
            ["Can UVs be optimized for mobile games?", "Yes. We can plan texture atlases, efficient packing, and material-friendly UV layouts for mobile performance."]
        ]
    },
    sculpting: {
        slug: "blender-sculpting",
        eyebrow: "Blender Sculpting",
        title: "Blender Sculpting for Game Characters, Creatures, Props & Detail Assets",
        description: "Oengines creates Blender sculpting for game characters, creatures, monsters, props, armor, weapons, faces, organic forms, and high-detail models used for baking or presentation.",
        keywords: "Blender sculpting, game character sculpting, creature sculpting for games, high detail sculpting, Blender organic modeling, sculpted game assets",
        layout: "relic",
        heroImage: "assets/img/GameDevelopment/Creative-UI-Character-Design.png",
        stats: ["Creatures", "Armor", "Faces", "Details"],
        services: [
            ["Character Sculpting", "Faces, anatomy, stylized bodies, clothing folds, armor details, hair forms, and expression-friendly sculpted forms."],
            ["Creature Sculpting", "Monsters, animals, fantasy creatures, bosses, claws, horns, scales, wings, tails, and organic detail work."],
            ["Prop & Armor Detail", "Weapons, shields, armor, relics, collectibles, stone, metal damage, engravings, and high-detail surface forms."],
            ["Bake Workflow Support", "High-poly sculpt organization, detail separation, naming, and handoff support for normal-map baking."]
        ],
        showcase: {
            title: "High-Detail Sculpt Direction",
            text: "Sculpting helps define character personality, creature threat, armor detail, and premium asset quality before optimization or baking.",
            items: ["Organic form detail", "Creature surface language", "Armor and relic details", "Bake-ready high-poly files"]
        },
        production: [
            ["Sculpt Brief", "We define style, anatomy, creature language, detail level, and gameplay visibility.", "Direction"],
            ["Primary Forms", "Large shapes, silhouette, anatomy, armor, or creature forms are sculpted first.", "Sculpt"],
            ["Detail Pass", "Fine details, surface damage, skin folds, scales, engravings, and wear are added.", "Detail"],
            ["Bake Prep", "High-poly assets are organized for baking, retopology, or presentation workflows.", "Handoff"]
        ],
        pipeline: ["Sculpt brief", "Primary forms", "Secondary forms", "Detail polish", "Bake prep"],
        deliverables: ["High-poly sculpt", "Character sculpt", "Creature sculpt", "Armor details", "Prop sculpt", "Bake notes", "Blender files", "Presentation renders"],
        faqs: [
            ["Do you sculpt game assets in Blender?", "Yes. We create Blender sculpts for characters, creatures, props, armor, weapons, and detail assets."],
            ["Can sculpts be used for normal map baking?", "Yes. We can organize high-poly sculpts for baking and handoff to game-ready asset workflows."],
            ["Can you sculpt stylized creatures?", "Yes. We can create stylized, fantasy, horror, casual, or semi-realistic creature forms in Blender."]
        ]
    }
};

function Modeling3DPage({ page }) {
    const data = pages[page];
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": data.eyebrow,
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": data.eyebrow,
        "areaServed": "Worldwide",
        "description": data.description
    };

    return (
        <>
            <Helmet>
                <title>{data.title} | Oengines Games LLP</title>
                <meta name="description" content={data.description} />
                <meta name="keywords" content={data.keywords} />
                <link rel="canonical" href={`https://www.oengines.com/${data.slug}`} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className={`modeling3d-page modeling3d-redesign modeling3d-${data.layout}`}>
                <section className="modeling3d-hero-v2">
                    <div className="modeling3d-v2-gridline" />
                    <div className="container">
                        <div className="modeling3d-hero-v2-grid">
                            <div className="modeling3d-hero-v2-copy">
                                <span className="modeling3d-eyebrow">Blender Game Asset Studio</span>
                                <h1>{data.title}</h1>
                                <p>{data.description}</p>
                                <div className="modeling3d-actions">
                                    <a href="/contact" className="as-btn">Plan My 3D Asset <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="modeling3d-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="modeling3d-hero-v2-media">
                                <div className="modeling3d-hero-v2-label">{data.eyebrow}</div>
                                <img src={data.heroImage} alt={data.title} />
                                <div className="modeling3d-hero-v2-stats">
                                    {data.stats.map((item) => <span key={item}>{item}</span>)}
                                </div>
                            </div>
                            <aside className="modeling3d-hero-v2-brief">
                                <span>Asset Brief</span>
                                <h2>{data.showcase.title}</h2>
                                <p>{data.showcase.text}</p>
                            </aside>
                        </div>
                    </div>
                </section>

                <section className="modeling3d-asset-console">
                    <div className="container">
                        <div className="modeling3d-console-grid">
                            <div className="modeling3d-console-title">
                                <span className="modeling3d-section-kicker">Production Console</span>
                                <h2>What This Page Covers</h2>
                            </div>
                            <div className="modeling3d-console-items">
                                {data.showcase.items.map((item, index) => (
                                    <article key={item}>
                                        <strong>{String(index + 1).padStart(2, "0")}</strong>
                                        <span>{item}</span>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="modeling3d-capability-section space">
                    <div className="container-fluid px-xxl-5">
                        <div className="modeling3d-wide-heading">
                            <span>Blender Modeling Capabilities</span>
                            <h2>Built for Game Production, Store Presentation, Optimization, and Developer Handoff</h2>
                        </div>
                        <div className="modeling3d-capability-board">
                            {data.services.map(([title, text], index) => (
                                <article key={title} className={index === 0 ? "is-featured" : ""}>
                                    <div className="modeling3d-card-number">{String(index + 1).padStart(2, "0")}</div>
                                    <h3>{title}</h3>
                                    <p>{text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="modeling3d-workflow-section space">
                    <div className="container">
                        <div className="modeling3d-workflow-shell">
                            <div className="modeling3d-workflow-heading">
                                <span>Production Plan</span>
                                <h2>{data.eyebrow} Workflow</h2>
                                <p>Our Blender workflow keeps style direction, game constraints, optimization, naming, export formats, and implementation notes connected from the beginning.</p>
                            </div>
                            <div className="modeling3d-workflow-lanes">
                                {data.production.map((row, index) => (
                                    <article key={row[0]}>
                                        <div>
                                            <strong>{String(index + 1).padStart(2, "0")}</strong>
                                            <span>{row[2]}</span>
                                        </div>
                                        <h3>{row[0]}</h3>
                                        <p>{row[1]}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="modeling3d-pipeline-v2-section space">
                    <div className="container">
                        <div className="modeling3d-pipeline-v2-header">
                            <span>Blender Pipeline</span>
                            <h2>Asset Flow from Brief to Export</h2>
                        </div>
                        <div className="modeling3d-pipeline-v2">
                            {data.pipeline.map((item, index) => (
                                <article key={item}>
                                    <strong>{String(index + 1).padStart(2, "0")}</strong>
                                    <span>{item}</span>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="modeling3d-vault-section space">
                    <div className="container">
                        <div className="modeling3d-vault-grid">
                            <div className="modeling3d-vault-copy">
                                <span>Deliverables Vault</span>
                                <h2>Game-Ready Files, References, and Handoff Notes</h2>
                                <p>Deliverables are scoped by project, but every page is organized around assets a game team can inspect, implement, optimize, or expand later.</p>
                            </div>
                            <div className="modeling3d-vault-list">
                                {data.deliverables.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="modeling3d-faq-v2-section space">
                    <div className="container">
                        <div className="modeling3d-faq-v2-grid">
                            <div className="modeling3d-faq-v2-title">
                                <span>FAQ</span>
                                <h2>{data.eyebrow} Questions</h2>
                                <a href="/contact" className="as-btn">Start 3D Modeling Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                            </div>
                            <div className="modeling3d-faq-v2-list">
                                {data.faqs.map(([question, answer]) => (
                                    <article key={question}>
                                        <h3>{question}</h3>
                                        <p>{answer}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}

export const CharacterModeling = () => <Modeling3DPage page="character" />;
export const EnvironmentModeling = () => <Modeling3DPage page="environment" />;
export const PropsWeaponsModeling = () => <Modeling3DPage page="props" />;
export const VehicleModeling = () => <Modeling3DPage page="vehicles" />;
export const ArchitecturalModeling = () => <Modeling3DPage page="architecture" />;
export const LowHighPolyAssets = () => <Modeling3DPage page="lowhigh" />;
export const PbrTexturing = () => <Modeling3DPage page="pbr" />;
export const UvMapping = () => <Modeling3DPage page="uv" />;
export const BlenderSculpting = () => <Modeling3DPage page="sculpting" />;
